import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  CreditCard,
  Send,
  Receipt,
  TrendingUp,
  ArrowUpRight,
  ArrowDownLeft,
  PiggyBank,
  Eye,
  EyeOff,
  Plus,
  ChevronRight,
  DollarSign,
  Bell,
  Search,
  MoreVertical,
  Download,
  ArrowRight,
  Menu,
  X,
  Loader2,
  RefreshCw,
  LogOut
} from 'lucide-react'
import TransferModal from '../components/TransferModal'
import BillsModal from '../components/BillsModal'
import LoanModal from '../components/LoanModal'
import InvestmentModal from '../components/InvestmentModal'
import Sidebar from '../components/Sidebar'
import GoogleTranslate from '../components/GoogleTranslate'
import NotificationsCenter from './NotificationsCenter'
import BudgetManager from './BudgetManager'
import CryptoWallet from './CryptoWallet'
import RewardsCenter from './RewardsCenter'
import AccountsPage from './AccountsPage'
import TransactionsPage from './TransactionsPage'
import AnalyticsPage from './AnalyticsPage'
import InvestmentPage from './InvestmentPage'
import MobilePage from './MobilePage'
import HelpPage from './HelpPage'
import SettingsPage from './SettingsPage'
import TransferPage from './TransferPage'
import PayBillsPage from './PayBillsPage'
import KycPage from './KycPage'
import { AccountsAPI, TransactionsAPI, GoalsAPI, NotificationsAPI, LoansAPI, extractArray } from '../services/api'

export default function Dashboard({ user, onLogout }) {
  const { t, i18n } = useTranslation(['dashboard', 'common'])
  const [showBalance, setShowBalance] = useState(true)
  // Check if user needs KYC - if not approved, start with 'kyc' view
  const needsKyc = user?.kyc_status !== 'approved'
  const [activeView, setActiveView] = useState(needsKyc ? 'kyc' : 'dashboard')
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [showTransfer, setShowTransfer] = useState(false)
  const [showBills, setShowBills] = useState(false)
  const [showLoan, setShowLoan] = useState(false)
  const [showInvestment, setShowInvestment] = useState(false)
  const [kycRequired, setKycRequired] = useState(needsKyc)

  // Real data states
  const [accounts, setAccounts] = useState([])
  const [transactions, setTransactions] = useState([])
  const [goals, setGoals] = useState([])
  const [transactionSummary, setTransactionSummary] = useState(null)
  const [unreadNotifications, setUnreadNotifications] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loanApplications, setLoanApplications] = useState([])
  const [activeLoans, setActiveLoans] = useState([])
  const [loadingLoans, setLoadingLoans] = useState(false)
  const [error, setError] = useState(null)

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true)
      setError(null)
      try {
        const [accountsRes, transactionsRes, goalsRes, summaryRes, notifCountRes] = await Promise.all([
          AccountsAPI.getAll(),
          TransactionsAPI.getAll({ limit: 5 }),
          GoalsAPI.getAll({ status: 'active' }),
          TransactionsAPI.getSummary('month'),
          NotificationsAPI.getUnreadCount()
        ])

        if (accountsRes.success) {
          setAccounts(extractArray(accountsRes, 'accounts'))
        }
        if (transactionsRes.success) {
          setTransactions(extractArray(transactionsRes, 'transactions'))
        }
        if (goalsRes.success) {
          setGoals(extractArray(goalsRes).slice(0, 3))
        }
        if (summaryRes.success) {
          setTransactionSummary(summaryRes.data)
        }
        if (notifCountRes.success) {
          setUnreadNotifications(notifCountRes.data.count)
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err)
        setError('Failed to load dashboard data')
      } finally {
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [])

  const refreshData = async () => {
    setLoading(true)
    try {
      const [accountsRes, transactionsRes] = await Promise.all([
        AccountsAPI.getAll(),
        TransactionsAPI.getAll({ limit: 5 })
      ])
      if (accountsRes.success) {
        setAccounts(extractArray(accountsRes, 'accounts'))
      }
      if (transactionsRes.success) {
        setTransactions(extractArray(transactionsRes, 'transactions'))
      }
    } catch (err) {
      console.error('Error refreshing data:', err)
    } finally {
      setLoading(false)
    }
  }

  // Fetch loans data when viewing loans section
  const fetchLoansData = async () => {
    setLoadingLoans(true)
    try {
      const [applicationsRes, loansRes] = await Promise.all([
        LoansAPI.getApplications(),
        LoansAPI.getLoans()
      ])

      if (applicationsRes.success) {
        setLoanApplications(Array.isArray(applicationsRes.data) ? applicationsRes.data : [])
      }
      if (loansRes.success) {
        setActiveLoans(Array.isArray(loansRes.data) ? loansRes.data : [])
      }
    } catch (err) {
      console.error('Error fetching loans data:', err)
    } finally {
      setLoadingLoans(false)
    }
  }

  // Load loans data when switching to loans view
  useEffect(() => {
    if (activeView === 'loans') {
      fetchLoansData()
    }
  }, [activeView])

  // Handle navigation with KYC check
  const handleNavigation = (view) => {
    // Always allow navigation to KYC page
    if (view === 'kyc') {
      setActiveView(view)
      return
    }

    // If KYC is required but not completed, block navigation
    if (kycRequired && user?.kyc_status !== 'approved') {
      // Force user to KYC page
      setActiveView('kyc')
      return
    }

    setActiveView(view)
  }

  const quickActions = [
    {
      icon: <Send className="w-5 h-5" />,
      label: t('quickActions.transfer'),
      action: () => handleNavigation('transfer'),
      color: 'text-blue-600',
      bg: 'bg-blue-50'
    },
    {
      icon: <Receipt className="w-5 h-5" />,
      label: t('quickActions.payBill'),
      action: () => handleNavigation('paybills'),
      color: 'text-purple-600',
      bg: 'bg-purple-50'
    },
    {
      icon: <PiggyBank className="w-5 h-5" />,
      label: t('quickActions.deposit'),
      action: () => {},
      color: 'text-emerald-600',
      bg: 'bg-emerald-50'
    },
    {
      icon: <Download className="w-5 h-5" />,
      label: t('common:export'),
      action: () => {},
      color: 'text-slate-600',
      bg: 'bg-slate-50'
    }
  ]

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return renderDashboardContent()
      case 'accounts':
        return <AccountsPage onMenuClick={() => setMobileMenuOpen(true)} />
      case 'transactions':
        return <TransactionsPage onMenuClick={() => setMobileMenuOpen(true)} />
      case 'transfer':
        return <TransferPage onMenuClick={() => setMobileMenuOpen(true)} />
      case 'paybills':
        return <PayBillsPage onMenuClick={() => setMobileMenuOpen(true)} />
      case 'analytics':
        return <AnalyticsPage onMenuClick={() => setMobileMenuOpen(true)} />
      case 'notifications':
        return <NotificationsCenter onMenuClick={() => setMobileMenuOpen(true)} />
      case 'mobile':
        return <MobilePage onMenuClick={() => setMobileMenuOpen(true)} />
      case 'budgets':
        return <BudgetManager onMenuClick={() => setMobileMenuOpen(true)} />
      case 'investments':
        return <InvestmentPage onMenuClick={() => setMobileMenuOpen(true)} />
      case 'crypto':
        return <CryptoWallet onMenuClick={() => setMobileMenuOpen(true)} />
      case 'loans':
        return renderLoansContent()
      case 'rewards':
        return <RewardsCenter onMenuClick={() => setMobileMenuOpen(true)} />
      case 'help':
        return <HelpPage onMenuClick={() => setMobileMenuOpen(true)} />
      case 'settings':
        return <SettingsPage onMenuClick={() => setMobileMenuOpen(true)} />
      case 'kyc':
        return <KycPage onMenuClick={() => setMobileMenuOpen(true)} />
      default:
        return renderPlaceholderContent(activeView)
    }
  }

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-100 text-slate-600',
      submitted: 'bg-yellow-100 text-yellow-700',
      in_review: 'bg-orange-100 text-orange-700',
      approved: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
      funded: 'bg-blue-100 text-blue-700',
      active: 'bg-blue-100 text-blue-700',
      paid_off: 'bg-violet-100 text-violet-700',
      withdrawn: 'bg-slate-100 text-slate-600'
    }
    return styles[status] || 'bg-slate-100 text-slate-600'
  }

  const formatLoanStatus = (status) => {
    const labels = {
      draft: 'Draft',
      submitted: 'Submitted',
      in_review: 'Under Review',
      approved: 'Approved',
      rejected: 'Rejected',
      funded: 'Funded',
      active: 'Active',
      paid_off: 'Paid Off',
      withdrawn: 'Withdrawn'
    }
    return labels[status] || status
  }

  const renderLoansContent = () => (
    <div className="min-h-screen bg-white">
      <div className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="px-4 md:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-1">
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
              >
                <Menu className="w-6 h-6 text-slate-700" />
              </button>

              <div className="flex-1">
                <h1 className="text-xl md:text-2xl font-semibold text-slate-900">{t('loans:title')}</h1>
                <p className="text-xs md:text-sm text-slate-500 mt-0.5 hidden sm:block">{t('loans:subtitle')}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={fetchLoansData}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                disabled={loadingLoans}
              >
                <RefreshCw className={`w-5 h-5 text-slate-600 ${loadingLoans ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => setShowLoan(true)}
                className="flex items-center gap-2 px-3 md:px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-all text-xs md:text-sm font-medium justify-center"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">{t('loans:applyForLoan')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">
        {loadingLoans ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
          </div>
        ) : loanApplications.length === 0 && activeLoans.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <PiggyBank className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-sm font-semibold text-slate-900 mb-1">{t('loans:noActiveLoans')}</h3>
            <p className="text-xs text-slate-500 mb-4">{t('loans:applyToStart')}</p>
            <button
              onClick={() => setShowLoan(true)}
              className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-colors text-sm font-medium"
            >
              {t('loans:applyNow')}
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Loan Applications */}
            {loanApplications.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Loan Applications</h2>
                <div className="grid gap-4">
                  {loanApplications.map((app) => (
                    <div key={app.id} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-slate-900">{app.product_name}</h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(app.status)}`}>
                              {formatLoanStatus(app.status)}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-slate-500">Amount Requested</p>
                              <p className="font-semibold text-slate-900">${parseFloat(app.requested_amount).toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Term</p>
                              <p className="font-semibold text-slate-900">{app.term_months} months</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Interest Rate</p>
                              <p className="font-semibold text-slate-900">{app.offered_apr || '-'}% APR</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Monthly Payment</p>
                              <p className="font-semibold text-slate-900">${app.monthly_payment ? parseFloat(app.monthly_payment).toFixed(2) : '-'}</p>
                            </div>
                          </div>
                          {app.status === 'rejected' && app.rejection_reason && (
                            <div className="mt-3 p-3 bg-red-50 border border-red-100 rounded-lg">
                              <p className="text-sm text-red-700"><strong>Rejection Reason:</strong> {app.rejection_reason}</p>
                            </div>
                          )}
                          <p className="text-xs text-slate-400 mt-3">Application #{app.application_number} • {new Date(app.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Active Loans */}
            {activeLoans.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Active Loans</h2>
                <div className="grid gap-4">
                  {activeLoans.map((loan) => (
                    <div key={loan.id} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-slate-900">{loan.product_name}</h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(loan.status)}`}>
                              {formatLoanStatus(loan.status)}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-slate-500">Principal</p>
                              <p className="font-semibold text-slate-900">${parseFloat(loan.principal_amount).toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Current Balance</p>
                              <p className="font-semibold text-slate-900">${parseFloat(loan.current_balance).toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Monthly Payment</p>
                              <p className="font-semibold text-slate-900">${parseFloat(loan.monthly_payment).toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Next Payment</p>
                              <p className="font-semibold text-slate-900">{loan.next_payment_date ? new Date(loan.next_payment_date).toLocaleDateString() : '-'}</p>
                            </div>
                          </div>
                          <div className="mt-3">
                            <div className="flex justify-between text-xs text-slate-500 mb-1">
                              <span>Progress</span>
                              <span>{loan.payments_made} of {loan.payments_made + loan.payments_remaining} payments</span>
                            </div>
                            <div className="w-full bg-slate-100 rounded-full h-2">
                              <div
                                className="bg-emerald-500 h-2 rounded-full"
                                style={{ width: `${(loan.payments_made / (loan.payments_made + loan.payments_remaining)) * 100}%` }}
                              />
                            </div>
                          </div>
                          <p className="text-xs text-slate-400 mt-3">Loan #{loan.loan_number}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )

  const renderPlaceholderContent = (viewName) => (
    <div className="p-8 flex items-center justify-center min-h-screen bg-white">
      <div className="text-center max-w-md">
        <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <div className="text-3xl">🚧</div>
        </div>
        <h2 className="text-2xl font-semibold text-slate-900 mb-2 capitalize">
          {viewName.replace(/([A-Z])/g, ' $1').trim()}
        </h2>
        <p className="text-slate-500">{t('common:comingSoon')}</p>
      </div>
    </div>
  )

  // Calculate total balance from real accounts
  const totalBalance = accounts.reduce((sum, acc) => sum + parseFloat(acc.balance || 0), 0)

  // Calculate month-over-month change
  const monthChange = transactionSummary?.percentageChange || 0

  // Format transaction for display
  const formatTransaction = (tx) => {
    const isIncome = parseFloat(tx.amount) > 0
    return {
      id: tx.id,
      type: isIncome ? 'income' : 'expense',
      name: tx.description || tx.transaction_type,
      company: tx.merchant_name || tx.category_name || 'Transaction',
      amount: parseFloat(tx.amount),
      date: new Date(tx.transaction_date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      }),
      status: tx.status
    }
  }

  // Format account for display
  const formatAccount = (acc) => ({
    id: acc.id,
    name: acc.account_name || acc.type_name,
    number: `••${acc.account_number?.slice(-4) || '****'}`,
    balance: parseFloat(acc.balance || 0),
    type: acc.category?.toLowerCase() || 'checking'
  })

  const renderDashboardContent = () => (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      <div className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="px-4 md:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <Menu className="w-6 h-6 text-slate-700" />
            </button>

            <div className="flex-1 lg:flex-none">
              <h1 className="text-lg md:text-xl lg:text-2xl font-semibold text-slate-900">
                {t('greeting', { name: user.firstName || user.name })}
              </h1>
              <p className="text-xs md:text-sm text-slate-500 mt-0.5 hidden sm:block">
                {new Date().toLocaleDateString(i18n.language, { weekday: 'long', month: 'long', day: 'numeric' })}
              </p>
            </div>

            <div className="flex items-center gap-2 md:gap-3">
              <button
                onClick={refreshData}
                disabled={loading}
                className="p-2 md:p-2.5 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <RefreshCw className={`w-5 h-5 text-slate-600 ${loading ? 'animate-spin' : ''}`} />
              </button>

              <button
                onClick={() => setActiveView('notifications')}
                className="p-2 md:p-2.5 hover:bg-slate-100 rounded-lg transition-colors relative"
              >
                <Bell className="w-5 h-5 text-slate-600" />
                {unreadNotifications > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 bg-gradient-to-r from-amber-500 to-amber-600 rounded-full text-[10px] text-white flex items-center justify-center font-medium">
                    {unreadNotifications > 9 ? '9+' : unreadNotifications}
                  </span>
                )}
              </button>

              <button className="p-2 md:p-2.5 hover:bg-slate-100 rounded-lg transition-colors hidden sm:flex">
                <Search className="w-5 h-5 text-slate-600" />
              </button>

              {/* Language Selector */}
              <div className="hidden sm:block">
                <GoogleTranslate variant="minimal" />
              </div>

              {/* Logout Button */}
              <button
                onClick={onLogout}
                className="p-2 md:p-2.5 hover:bg-red-50 rounded-lg transition-colors group"
                title="Cerrar sesión"
              >
                <LogOut className="w-5 h-5 text-slate-600 group-hover:text-red-500 transition-colors" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">
        {/* Loading State */}
        {loading && accounts.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Loader2 className="w-8 h-8 text-amber-500 animate-spin mx-auto mb-4" />
              <p className="text-slate-500">{t('loadingDashboard')}</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={refreshData}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              {t('common:tryAgain')}
            </button>
          </div>
        ) : (
          /* Main Grid */
          <div className="grid lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
            {/* Left Column - 2/3 width */}
            <div className="lg:col-span-2 space-y-4 md:space-y-6 lg:space-y-8">
              {/* Total Balance Card */}
              <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-xl md:rounded-2xl p-5 md:p-6 lg:p-8 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 rounded-full filter blur-3xl opacity-20"></div>
                <div className="relative">
                  <div className="flex flex-col sm:flex-row items-start justify-between mb-6 md:mb-8 gap-4">
                    <div>
                      <p className="text-slate-400 text-xs md:text-sm font-medium mb-2">{t('totalBalance')}</p>
                      <div className="flex items-baseline gap-2 md:gap-3">
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight notranslate" translate="no">
                          {showBalance ? `$${totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '••••••'}
                        </h2>
                        <button
                          onClick={() => setShowBalance(!showBalance)}
                          className="p-1.5 md:p-2 hover:bg-white/10 rounded-lg transition-colors"
                        >
                          {showBalance ? <EyeOff className="w-3.5 h-3.5 md:w-4 md:h-4" /> : <Eye className="w-3.5 h-3.5 md:w-4 md:h-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="text-left sm:text-right">
                      <div className={`inline-flex items-center gap-1.5 ${monthChange >= 0 ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'} px-2.5 md:px-3 py-1 md:py-1.5 rounded-full text-xs md:text-sm font-semibold notranslate`} translate="no">
                        <TrendingUp className="w-3.5 h-3.5 md:w-4 md:h-4" />
                        {monthChange >= 0 ? '+' : ''}{monthChange.toFixed(1)}%
                      </div>
                      <p className="text-slate-400 text-xs mt-2">{t('vsLastMonth')}</p>
                    </div>
                  </div>

                  {/* Account Pills */}
                  <div className="flex flex-wrap gap-2 md:gap-3">
                    {accounts.slice(0, 3).map((account) => {
                      const formatted = formatAccount(account)
                      return (
                        <button
                          key={formatted.id}
                          onClick={() => setActiveView('accounts')}
                          className="flex items-center gap-2 md:gap-3 bg-white/10 hover:bg-white/15 backdrop-blur-sm px-3 md:px-4 py-2.5 md:py-3 rounded-lg md:rounded-xl transition-all group"
                        >
                          <div className="w-8 h-8 md:w-10 md:h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <CreditCard className="w-4 h-4 md:w-5 md:h-5" />
                          </div>
                          <div className="text-left">
                            <p className="text-xs text-slate-400">{formatted.name}</p>
                            <p className="text-sm md:text-base font-semibold notranslate" translate="no">
                              {showBalance ? `$${formatted.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '••••••'}
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block" />
                        </button>
                      )
                    })}
                    {accounts.length > 3 && (
                      <button
                        onClick={() => setActiveView('accounts')}
                        className="flex items-center gap-2 bg-white/10 hover:bg-white/15 backdrop-blur-sm px-4 py-3 rounded-xl transition-all"
                      >
                        <span className="text-sm font-medium">+{accounts.length - 3} more</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div>
                <h3 className="text-xs md:text-sm font-semibold text-slate-900 mb-3 md:mb-4">{t('quickActionsTitle')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
                  {quickActions.map((action, index) => (
                    <button
                      key={index}
                      onClick={action.action}
                      className="flex flex-col items-center gap-2 md:gap-3 p-3 md:p-4 bg-white border border-slate-200 hover:border-slate-300 rounded-lg md:rounded-xl transition-all hover:shadow-sm group"
                    >
                      <div className={`w-10 h-10 md:w-12 md:h-12 ${action.bg} ${action.color} rounded-lg md:rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                        {action.icon}
                      </div>
                      <span className="text-xs md:text-sm font-medium text-slate-700">{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Recent Activity */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-slate-900">{t('recentActivity')}</h3>
                  <button
                    onClick={() => setActiveView('transactions')}
                    className="text-sm text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1"
                  >
                    {t('common:viewAll')}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
                  {transactions.length === 0 ? (
                    <div className="p-8 text-center">
                      <p className="text-slate-500 text-sm">{t('noRecentTransactions')}</p>
                    </div>
                  ) : (
                    transactions.map((tx) => {
                      const transaction = formatTransaction(tx)
                      return (
                        <div
                          key={transaction.id}
                          className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-11 h-11 rounded-full flex items-center justify-center ${
                              transaction.type === 'income'
                                ? 'bg-emerald-50 text-emerald-600'
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {transaction.type === 'income' ? (
                                <ArrowDownLeft className="w-5 h-5" />
                              ) : (
                                <ArrowUpRight className="w-5 h-5" />
                              )}
                            </div>

                            <div>
                              <p className="text-sm font-semibold text-slate-900">{transaction.name}</p>
                              <p className="text-xs text-slate-500 mt-0.5">{transaction.company}</p>
                            </div>
                          </div>

                          <div className="text-right">
                            <p className={`text-sm font-semibold notranslate ${
                              transaction.type === 'income' ? 'text-emerald-600' : 'text-slate-900'
                            }`} translate="no">
                              {transaction.type === 'income' ? '+' : '-'}${Math.abs(transaction.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5">{transaction.date}</p>
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - 1/3 width */}
            <div className="space-y-4 md:space-y-6">
              {/* Spending Overview */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-sm font-semibold text-slate-900">{t('spending')}</h3>
                  <button
                    onClick={() => setActiveView('analytics')}
                    className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                  >
                    {t('common:details')}
                  </button>
                </div>

                <div className="space-y-4">
                  {(transactionSummary?.byCategory || [
                    { name: 'Shopping', amount: 1240, percent: 35, color: 'bg-blue-600' },
                    { name: 'Food & Dining', amount: 890, percent: 25, color: 'bg-purple-600' },
                    { name: 'Transport', amount: 620, percent: 18, color: 'bg-emerald-600' },
                    { name: 'Other', amount: 450, percent: 12, color: 'bg-slate-400' }
                  ]).slice(0, 4).map((item, index) => (
                    <div key={index}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-slate-700">{item.name || item.category_name}</span>
                        <span className="text-xs font-semibold text-slate-900 notranslate" translate="no">${parseFloat(item.amount || item.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${item.percent || item.percentage || 25}%` }}
                          transition={{ duration: 1, delay: index * 0.1 }}
                          className={`h-full ${item.color || ['bg-blue-600', 'bg-purple-600', 'bg-emerald-600', 'bg-slate-400'][index % 4]}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Goals */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-sm font-semibold text-slate-900">{t('goals')}</h3>
                  <button
                    onClick={() => setActiveView('budgets')}
                    className="w-6 h-6 bg-slate-100 hover:bg-slate-200 rounded-lg flex items-center justify-center transition-colors"
                  >
                    <Plus className="w-4 h-4 text-slate-600" />
                  </button>
                </div>

                <div className="space-y-4">
                  {goals.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-xs text-slate-500 mb-2">{t('noActiveGoals')}</p>
                      <button
                        onClick={() => setActiveView('budgets')}
                        className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                      >
                        {t('createGoal')}
                      </button>
                    </div>
                  ) : (
                    goals.map((goal) => {
                      const progress = (parseFloat(goal.current_amount) / parseFloat(goal.target_amount)) * 100
                      return (
                        <div key={goal.id}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-slate-700">{goal.name}</span>
                            <span className={`text-xs font-semibold notranslate ${progress >= 100 ? 'text-emerald-600' : 'text-amber-600'}`} translate="no">
                              {progress.toFixed(0)}%
                            </span>
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${Math.min(progress, 100)}%` }}
                              transition={{ duration: 1.2 }}
                              className={`h-full ${progress >= 100 ? 'bg-emerald-600' : 'bg-gradient-to-r from-amber-400 to-amber-600'}`}
                            />
                          </div>
                          <p className="text-xs text-slate-500 mt-1.5 notranslate" translate="no">
                            ${parseFloat(goal.current_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })} of ${parseFloat(goal.target_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                      )
                    })
                  )}
                </div>
              </div>

              {/* Insights */}
              <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-5 text-white shadow-lg shadow-amber-200/50">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5" />
                  </div>
                  <button className="text-white/80 hover:text-white">
                    <MoreVertical className="w-5 h-5" />
                  </button>
                </div>
                <h4 className="text-sm font-semibold mb-1">{t('monthlyInsight')}</h4>
                <p className="text-xs text-white/80 leading-relaxed">
                  {monthChange >= 0
                    ? t('insightPositive', { percent: monthChange.toFixed(1) })
                    : t('insightNegative', { percent: Math.abs(monthChange).toFixed(1) })
                  }
                </p>
              </div>

              {/* User Tier Badge */}
              {user.tier && (
                <div className={`rounded-xl p-5 ${
                  user.tier === 'diamond' ? 'bg-gradient-to-br from-purple-500 to-indigo-600' :
                  user.tier === 'gold' ? 'bg-gradient-to-br from-yellow-500 to-orange-600' :
                  user.tier === 'silver' ? 'bg-gradient-to-br from-slate-400 to-slate-600' :
                  'bg-gradient-to-br from-slate-700 to-slate-900'
                } text-white`}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <DollarSign className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-xs text-white/80">{t('memberStatus')}</p>
                      <p className="text-lg font-bold capitalize">{t('tierLevel', { tier: user.tier })}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setActiveView('rewards')}
                    className="mt-4 w-full py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors"
                  >
                    {t('viewRewards')}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )

  const handleViewChange = (view) => {
    handleNavigation(view)
    setMobileMenuOpen(false)
  }

  return (
    <div className="flex min-h-screen bg-white">
      <Sidebar
        activeView={activeView}
        onViewChange={handleViewChange}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        isMobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
        kycRequired={kycRequired && user?.kyc_status !== 'approved'}
      />

      <div className="flex-1 overflow-auto w-full lg:w-auto">
        {renderContent()}
      </div>

      {/* Modals */}
      {showTransfer && (
        <TransferModal
          onClose={() => setShowTransfer(false)}
          user={user}
          onTransfer={() => { setShowTransfer(false); refreshData(); }}
        />
      )}

      {showBills && (
        <BillsModal
          onClose={() => setShowBills(false)}
          user={user}
          onPayBill={() => { setShowBills(false); refreshData(); }}
        />
      )}

      {showLoan && (
        <LoanModal
          onClose={() => setShowLoan(false)}
          user={user}
          onApplyLoan={() => setShowLoan(false)}
        />
      )}

      {showInvestment && (
        <InvestmentModal
          onClose={() => setShowInvestment(false)}
          user={user}
          onInvest={() => { setShowInvestment(false); refreshData(); }}
        />
      )}
    </div>
  )
}
